import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { KnowledgeBase } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { ArrowLeft, Edit, Trash2, Calendar, Tag } from 'lucide-react';
import { safeFormatDate, DATE_FORMATS } from '../utils/dateHelpers.js';

export default function ViewKnowledge() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const articleId = searchParams.get('id');
  
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    if (articleId) {
      loadArticle();
      loadUser();
    } else {
      navigate(createPageUrl('Knowledge'));
    }
  }, [articleId]);

  const loadUser = async () => {
    try {
      const userData = await User.me();
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadArticle = async () => {
    setIsLoading(true);
    try {
      const articleData = await KnowledgeBase.findById(articleId);
      setArticle(articleData);
      
      // Load related articles (based on same category)
      if (articleData.category) {
        try {
          const allArticles = await KnowledgeBase.list({ category: articleData.category });
          const filtered = allArticles.filter(a => a.id !== articleId).slice(0, 3);
          setRelatedArticles(filtered);
        } catch (relatedError) {
          console.error('Error loading related articles:', relatedError);
          // Não impede o carregamento do artigo principal se houver erro nos relacionados
          setRelatedArticles([]);
        }
      }
    } catch (error) {
      console.error('Error loading article:', error);
      navigate(createPageUrl('Knowledge'));
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja excluir este artigo?')) return;
    
    try {
      await KnowledgeBase.delete(articleId);
      navigate(createPageUrl('Knowledge'));
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Erro ao excluir artigo. Por favor, tente novamente.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Knowledge'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{article.title}</h1>
          <div className="flex items-center gap-3 mt-1 text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>{safeFormatDate(article.created_at, DATE_FORMATS.PRETTY)}</span>
            </div>
            <Badge variant="secondary">{article.category}</Badge>
          </div>
        </div>
        
        {currentUser && currentUser.role !== 'client' && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(createPageUrl(`EditKnowledge?id=${articleId}`))}
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 grid-cols-1 lg:grid-cols-4">
        <Card className="lg:col-span-3">
          <CardContent className="pt-6">
            {/* Renderiza o conteúdo HTML diretamente */}
            <div 
              dangerouslySetInnerHTML={{ __html: article.content }}
              className="prose max-w-none article-content"
              style={{
                lineHeight: '1.6',
                fontSize: '16px',
                color: 'inherit'
              }}
            />
          </CardContent>
        </Card>

        <div className="space-y-6">
          {article.tags && article.tags.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, index) => (
                    <Badge key={index} variant="outline">{tag}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {relatedArticles.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Artigos Relacionados</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {relatedArticles.map((relatedArticle) => (
                  <div 
                    key={relatedArticle.id}
                    className="border-b last:border-b-0 pb-2 last:pb-0 cursor-pointer hover:text-primary"
                    onClick={() => navigate(createPageUrl(`ViewKnowledge?id=${relatedArticle.id}`))}
                  >
                    <p className="font-medium">{relatedArticle.title}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}